import { useEffect, useRef, useLayoutEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchChatList, subscribeToChatList } from "./ChatThunk";
import * as chatV2 from "../../../modules/chat/redux/ChatSlice.v2";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { RootState } from "src/setup/redux/store";
import { auditChatRead } from "../utils/firestoreReadAudit";
import {
  applyStoredChatListCollaboration,
  readStoredChatList,
  removeStoredChatListCollaboration,
} from "../services/chatListStorageCache";

type ChatType = "allChat" | "myChat";

export const useChatListSubscriber = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const audioRef = useRef<HTMLAudioElement>();
  const lastToastMapRef = useRef<Record<string, number>>({});
  const lastToastTimeRef = useRef<Record<string, number>>({});
  const TOAST_THROTTLE_MS = 3000;
  const lastFetchedAtRef = useRef<Record<string, number>>({});
  const EXPIRY_MS = 60 * 1000 * 3; // 3 minute
  const { activeTab } = useSelector((state: RootState) => state.ChatV2);

  // Get both chat type states directly
  const allChatOrder = useSelector(
    (state: RootState) => state.ChatV2.allChat?.collaborationOrder ?? [],
    shallowEqual
  );
  const myChatOrder = useSelector(
    (state: RootState) => state.ChatV2.myChat?.collaborationOrder ?? [],
    shallowEqual
  );
  const allChatNewestKnownLastInteractionAt = useSelector(
    (state: RootState) => state.ChatV2.allChat?.newestKnownLastInteractionAt,
    shallowEqual
  );
  const myChatNewestKnownLastInteractionAt = useSelector(
    (state: RootState) => state.ChatV2.myChat?.newestKnownLastInteractionAt,
    shallowEqual
  );

  // Use refs to access latest state inside effects without adding to deps
  const allChatOrderRef = useRef(allChatOrder);
  const myChatOrderRef = useRef(myChatOrder);
  const newestKnownLastInteractionAtRef = useRef<Record<string, any>>({});

  useLayoutEffect(() => {
    allChatOrderRef.current = allChatOrder;
    myChatOrderRef.current = myChatOrder;
    newestKnownLastInteractionAtRef.current = {
      allChat: allChatNewestKnownLastInteractionAt,
      myChat: myChatNewestKnownLastInteractionAt,
    };
  });

  useEffect(() => {
    if (typeof Audio !== "undefined") {
      audioRef.current = new Audio("/media/misc/audio/notification.mp3");
    }
  }, []);

  useEffect(() => {
    const type: ChatType = activeTab;
    const now = Date.now();
    const lastFetchedAt = lastFetchedAtRef.current[type] ?? 0;
    const isExpired = now - lastFetchedAt > EXPIRY_MS;

    // Check collaborationOrder for the active type using refs
    const collaborationOrderForType =
      type === "allChat" ? allChatOrderRef.current : myChatOrderRef.current;
    const alreadyFetched =
      Array.isArray(collaborationOrderForType) && collaborationOrderForType.length > 0;
    const storedList = alreadyFetched ? null : readStoredChatList(type);
    const listenerNewestKnownLastInteractionAt =
      newestKnownLastInteractionAtRef.current[type] ??
      storedList?.newestKnownLastInteractionAt ??
      null;

    if (isExpired || !alreadyFetched) {
      auditChatRead({
        event: "chat_list_fetch_trigger",
        source: "chat_list_subscriber_mount",
        chatType: type,
        estimatedReads: 0,
        details: {
          activeTab,
          alreadyFetched,
          isExpired,
        },
      });
      dispatch(fetchChatList({ chatType: type, queryMode: "initial" }));
      lastFetchedAtRef.current[type] = now;
    }

    auditChatRead({
      event: "chat_list_listener_subscribe",
      source: listenerNewestKnownLastInteractionAt
        ? "chat_list_subscriber_mount_newer"
        : "chat_list_subscriber_mount",
      chatType: type,
      estimatedReads: 0,
      details: {
        activeTab,
        listenerMode: listenerNewestKnownLastInteractionAt ? "newer" : "initial",
        usedStoredCursor: Boolean(storedList?.newestKnownLastInteractionAt),
      },
    });

    const unsubscribe = subscribeToChatList(
      type,
      (collab, changeType) => {
        if (changeType !== "removed") {
          dispatch(chatV2.updateCollaboration({ chatType: type, data: collab }));
          applyStoredChatListCollaboration(type, collab);
          dispatch(
            chatV2.setChatListCursor({
              chatType: type,
              newestKnownLastInteractionAt: collab.lastInteractionAt,
              lastFetchedAt: Date.now(),
            })
          );
        } else {
          dispatch(
            chatV2.removeCollaboration({
              chatType: type,
              id: collab?.id,
            })
          );
          removeStoredChatListCollaboration(type, collab.id);
        }

        const collabId = collab.id;
        const lastInteractionAt = collab.lastInteractionAt?.seconds ?? 0;

        const now = Date.now();
        const lastShown = lastToastMapRef.current[collabId];
        const lastToastTime = lastToastTimeRef.current[collabId] ?? 0;

        // Skip if we already showed this interaction recently
        if (
          lastShown === lastInteractionAt &&
          now - lastToastTime < TOAST_THROTTLE_MS
        )
          return;

        // Only show toast for messages less than 30 seconds old
        const MESSAGE_FRESHNESS_MS = 30 * 1000;
        const messageAge = now - (lastInteractionAt * 1000);
        if (messageAge > MESSAGE_FRESHNESS_MS) {
          return;
        }

        lastToastMapRef.current[collabId] = lastInteractionAt;
        lastToastTimeRef.current[collabId] = now;

        const profile =
          collab?.profileName ??
          collab?.userSearchKeys?.[collab?.lastInteractionChannel] ??
          "";

        // Get appropriate message text based on message type
        const getMessageText = () => {
          const messageType = collab?.lastInteractionType?.toLowerCase();
          const msg = collab?.lastInteractionMessage ?? "";

          // If there's a text message, show it
          if (msg && (messageType === "text" || messageType === "button" || messageType === "faq_liveagent")) {
            return msg;
          }

          // For non-text messages, show type-specific labels
          switch (messageType) {
            case "image":
            case "image_with_text":
              return t("Message.Type.Image", "📷 Image");
            case "video":
              return t("Message.Type.Video", "🎥 Video");
            case "audio":
              return t("Message.Type.Audio", "🎵 Audio");
            case "document":
              return t("Message.Type.Document", "📄 Document");
            case "sticker":
              return t("Message.Type.Sticker", "Sticker");
            case "location":
              return t("Message.Type.Location", "📍 Location");
            case "order":
            case "order_card":
              return t("Message.Type.Order", "🛒 Order");
            case "template":
            case "interactive":
              return t("Message.Type.Template", "Template");
            case "product":
              return t("Message.Type.Product", "🛍️ Product");
            default:
              return msg || t("Message.Type.NewMessage", "New message");
          }
        };

        if (collab?.lastInteractionDestination === "inbound") {
          toast.info(`${t("Notif.Info.Message")} ${profile} : ${getMessageText()}`);
          audioRef.current?.play().catch(() => {});
        }
      },
      { newestKnownLastInteractionAt: listenerNewestKnownLastInteractionAt }
    );

    return () => {
      auditChatRead({
        event: "chat_list_listener_unsubscribe",
        source: "chat_list_subscriber_unmount",
        chatType: type,
        estimatedReads: 0,
        details: { activeTab },
      });
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dispatch, t]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const now = Date.now();
        const type: ChatType = activeTab;
        const lastFetchedAt = lastFetchedAtRef.current[type] ?? 0;
        const isExpired = now - lastFetchedAt > EXPIRY_MS;

        if (isExpired) {
          auditChatRead({
            event: "chat_list_fetch_trigger",
            source: "visibility_refresh",
            chatType: type,
            estimatedReads: 0,
            details: {
              activeTab,
              lastFetchedAt,
              expiryMs: EXPIRY_MS,
            },
          });
          dispatch(
            fetchChatList({
              chatType: type,
              queryMode: "refresh",
              newestKnownLastInteractionAt:
                newestKnownLastInteractionAtRef.current[type],
            })
          );
          lastFetchedAtRef.current[type] = now;
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [activeTab, dispatch]);
};
